import { Link, Navigate, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { usePageMeta } from '@/hooks/usePageMeta';
import { LEGAL_PAGES, type LegalPageContent } from '@/content/legalPages';

const SLUGS = Object.keys(LEGAL_PAGES) as LegalPageContent['slug'][];

const SHORT_LABEL: Record<LegalPageContent['slug'], string> = {
  impressum: 'Impressum',
  datenschutz: 'Datenschutz',
  agb: 'AGB',
};

function slugFromPath(pathname: string): LegalPageContent['slug'] | null {
  const key = pathname.replace(/^\//, '') as LegalPageContent['slug'];
  return SLUGS.includes(key) ? key : null;
}

const LegalPage = () => {
  const { pathname } = useLocation();
  const slug = slugFromPath(pathname);
  const page = slug ? LEGAL_PAGES[slug] : null;

  usePageMeta({
    title: page?.metaTitle ?? 'Yawas Kebab',
    description: page?.metaDescription ?? '',
  });

  if (!slug || !page) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />
      <main className="pt-28 pb-20 md:pt-32 md:pb-28">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <p className="text-amber-600 text-sm font-semibold tracking-widest uppercase mb-3">
            Rechtliches
          </p>
          <h1
            className="text-3xl md:text-5xl font-bold text-stone-900"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {page.title}
          </h1>
          <p className="text-stone-400 text-sm mt-3 mb-10">{page.updatedLabel}</p>

          <div className="space-y-10">
            {page.sections.map((section) => (
              <section key={section.heading}>
                <h2 className="text-lg md:text-xl font-semibold text-stone-900 mb-3">
                  {section.heading}
                </h2>
                {section.paragraphs?.map((text, i) => (
                  <p
                    key={`${section.heading}-${i}`}
                    className="text-stone-600 text-sm md:text-base leading-relaxed mb-3 whitespace-pre-line"
                  >
                    {text}
                  </p>
                ))}
                {section.bullets && section.bullets.length > 0 && (
                  <ul className="list-disc pl-5 space-y-2 text-stone-600 text-sm md:text-base leading-relaxed">
                    {section.bullets.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                )}
              </section>
            ))}
          </div>

          <div className="mt-14 pt-8 border-t border-stone-200 flex flex-wrap gap-4 text-sm">
            {SLUGS.filter((s) => s !== slug).map((s) => (
              <Link
                key={s}
                to={`/${s}`}
                className="text-amber-600 hover:text-amber-700 font-medium transition-colors"
              >
                {SHORT_LABEL[s]}
              </Link>
            ))}
            <Link to="/" className="text-stone-500 hover:text-stone-700 transition-colors">
              Zur Startseite
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default LegalPage;
